/**
 * Velocity Tier Utilities
 *
 * Centralized mapping between delivery speed tiers and pacing multipliers.
 * Used by both UI and routing logic to avoid drift.
 * Note: "bid" values are internal pacing basis numbers, not prices.
 */

export type VelocityTier = "conservative" | "balanced" | "aggressive" | "maximum";

export const VELOCITY_TIER_MULTIPLIERS: Record<VelocityTier, number> = {
  conservative: 0.85,
  balanced: 1.0,
  aggressive: 1.2,
  maximum: 1.4,
};

// Map bid values to velocity tiers using a baseline min price.
export function bidToVelocityTier(bidValue: number, minPrice: number = 20): VelocityTier {
  const ratio = bidValue / minPrice;
  if (ratio >= 2.0) return "maximum";
  if (ratio >= 1.5) return "aggressive";
  if (ratio >= 1.0) return "balanced";
  return "conservative";
}

// Map velocity tiers back to bid values using a baseline min price.
export function velocityTierToBid(tier: VelocityTier, minPrice: number = 20): number {
  switch (tier) {
    case "maximum":
      return Math.round(minPrice * 2.0);
    case "aggressive":
      return Math.round(minPrice * 1.5);
    case "balanced":
      return Math.round(minPrice * 1.0);
    case "conservative":
      return Math.round(minPrice * 0.7);
    default:
      return Math.round(minPrice);
  }
}

export function velocityTierToMultiplier(tier: VelocityTier): number {
  return VELOCITY_TIER_MULTIPLIERS[tier] ?? 1.0;
}

export function getVelocityMultiplierFromBid(bidValue: number, minPrice: number = 20): number {
  return velocityTierToMultiplier(bidToVelocityTier(bidValue, minPrice));
}
